/**
 * 租户级业务 Store 重置登记簿（跨租户数据隔离的统一入口）。
 *
 * <h3>背景</h3>
 * <p>多账号场景下，同一台设备可能先后登录不同学校（租户）的账号。切换账号 / 登出时，
 * 必须把"学校专属"的业务数据从内存与本地存储中清掉，否则上一个学校的会话、课表等
 * 会残留在下一个学校的界面上，造成跨租户数据泄漏（参见 AI 会话泄漏事故）。</p>
 *
 * <h3>用法</h3>
 * <p>每个"学校专属"Store 在自身文件末尾调用 {@link registerTenantScopedReset} 自我登记：
 * <pre>
 *   registerTenantScopedReset('aiSessions', () => useAiSessionsStore().resetAll())
 * </pre>
 * 切换租户 / 登出时，{@link resetAllTenantScopedStores} 会遍历登记簿逐一重置。</p>
 *
 * <h3>为什么不直接"全清"</h3>
 * <p>并非所有 Store 都该清——账号列表、主题、侧栏折叠等是全局/设备级偏好，必须保留。
 * 因此采用"自我登记"制：学校专属的 Store 主动登记，全局 Store 不登记。新增学校专属
 * 模块时只需在本文件登记一句，即可自动纳入清理，不会再"漏登记"。</p>
 *
 * @module stores/_tenantScopedRegistry
 */

import { logger } from '@/utils/logger'

type ResetFn = () => void | Promise<void>

/** 已登记的重置函数（按 store 名去重，HMR 重复执行也会覆盖而非追加）。 */
const entries = new Map<string, ResetFn>()

/**
 * 登记一个"学校专属"Store 的重置函数。
 *
 * @param name  Store 名（仅用于日志/排查，需唯一）
 * @param reset 重置函数；会在切换租户 / 登出时被调用。应同时清空内存状态与本地存储键。
 */
export function registerTenantScopedReset(name: string, reset: ResetFn): void {
  entries.set(name, reset)
}

/**
 * 重置全部已登记的学校专属 Store。
 *
 * <p>单个 Store 重置失败不会中断其余 Store 的重置（隔离故障域）。切租户的完整性比
 * 单点成败更重要。</p>
 */
export async function resetAllTenantScopedStores(): Promise<void> {
  for (const [name, reset] of entries) {
    try {
      await reset()
    } catch (err) {
      // logger 不依赖任何 store，此处不会产生循环依赖；生产环境静默，诊断信息由重置流程外层兜底。
      logger.warn(`[tenantScopedRegistry] 重置 "${name}" 失败:`, err)
    }
  }
}

/** 仅供测试 / 排查：列出当前已登记的 Store 名。 */
export function listRegisteredTenantScopedStores(): string[] {
  return Array.from(entries.keys())
}
